import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BastionApiClient } from '@bastion/api';
import type { paths } from '@bastion/api';
import { authenticatedFetch } from '../../authenticatedFetch';
import {
  invalidateLabelQueries,
  invalidateSegmentQueries,
} from './invalidation';
import { aiPendingSegmentsKey, cloudEpisodeKey, episodeSegmentsKey } from './keys';
import type { EpisodeSegment } from './types';

/**
 * The wire types come from the server's OpenAPI schema, so a route change
 * fails the type check here. Regenerate them with
 * `npm run --workspace=@bastion/api generate-types`.
 */
type SegmentListResponse =
  paths['/episodes/{episode_id}/segments']['get']['responses']['200']['content']['application/json'];

/**
 * The largest page `GET /episodes/{id}/segments` serves. An episode's segments
 * tile its timeline, and every caller wants all of them, so read one page at
 * the route's maximum rather than page through it. A segment is at least 30
 * frames, so 500 of them cover 15000 frames, longer than any episode operators
 * label.
 */
const SEGMENT_PAGE_LIMIT = 500;

/**
 * Read one page of an episode's segments, made by these methods.
 *
 * An episode that holds more segments than one page loses its last ones, and
 * the timeline would show a gap with no error. The envelope reports the real
 * count, so compare it against the page and warn when the two differ.
 *
 * `prefetchReviewEpisode` seeds the cache entry `useEpisodeSegments` serves, so
 * it reads through this function too. A second reader with its own page size
 * would seed a truncated entry that this warning never sees.
 */
export async function readSegments(
  episodeId: string,
  methods: string[],
  signal?: AbortSignal,
): Promise<EpisodeSegment[]> {
  const params = new URLSearchParams({ limit: String(SEGMENT_PAGE_LIMIT) });
  for (const method of methods) params.append('method', method);
  const resp = await authenticatedFetch<SegmentListResponse>(
    `/episodes/${encodeURIComponent(episodeId)}/segments?${params.toString()}`,
    { signal },
  );
  if (resp.total > resp.items.length) {
    // The page already asks for the route's maximum, so a larger request
    // fails validation. Reading the rest means paging.
    console.warn(
      `Episode ${episodeId} holds ${resp.total} segments and this read returned ` +
        `${resp.items.length}. Page the read to show the rest.`,
    );
  }
  return resp.items as EpisodeSegment[];
}

/**
 * The segments an episode carries on its labeled timeline.
 *
 * The server returns them only for an episode the caller's organization holds
 * a grant on. An episode the caller cannot reach answers the same as one that
 * does not exist.
 */
export function useEpisodeSegments(episodeId: string | undefined) {
  return useQuery({
    queryKey: episodeSegmentsKey(episodeId),
    queryFn: async ({ signal }): Promise<EpisodeSegment[]> => {
      if (!episodeId) return [];
      return readSegments(episodeId, ['manual', 'ai'], signal);
    },
    enabled: !!episodeId,
  });
}

export function useCreateSegment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      episodeId: string;
      startFrame: number;
      endFrame: number;
      label: string;
      method?: string;
    }) => {
      return authenticatedFetch<EpisodeSegment>(
        `/episodes/${encodeURIComponent(params.episodeId)}/segments`,
        {
          method: 'POST',
          body: JSON.stringify({
            start_frame: params.startFrame,
            end_frame: params.endFrame,
            label: params.label,
            method: params.method ?? 'manual',
          }),
        },
      );
    },
    onSuccess: (_data, variables) => {
      invalidateSegmentQueries(queryClient, variables.episodeId);
      invalidateLabelQueries(queryClient);
    },
  });
}

export function useUpdateSegment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      episodeId: string;
      segmentId: string;
      startFrame: number;
      endFrame: number;
    }) => {
      return authenticatedFetch<EpisodeSegment>(
        `/episodes/${encodeURIComponent(params.episodeId)}/segments/${encodeURIComponent(params.segmentId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            start_frame: params.startFrame,
            end_frame: params.endFrame,
          }),
        },
      );
    },
    onSuccess: (_data, variables) => {
      invalidateSegmentQueries(queryClient, variables.episodeId);
    },
  });
}

export function useUpdateSegmentQuality() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      episodeId: string;
      segmentId: string;
      quality: 0 | 1 | 3 | 5;
    }) => {
      return authenticatedFetch<EpisodeSegment>(
        `/episodes/${encodeURIComponent(params.episodeId)}/segments/${encodeURIComponent(params.segmentId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            quality: params.quality,
          }),
        },
      );
    },
    onSuccess: (_data, variables) => {
      invalidateSegmentQueries(queryClient, variables.episodeId);
    },
  });
}

export function useDeleteSegment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { episodeId: string; segmentId: string }) => {
      await authenticatedFetch(
        `/episodes/${encodeURIComponent(params.episodeId)}/segments/${encodeURIComponent(params.segmentId)}`,
        { method: 'DELETE' },
      );
    },
    onSuccess: (_data, variables) => {
      invalidateSegmentQueries(queryClient, variables.episodeId);
      invalidateLabelQueries(queryClient);
    },
  });
}

/** The AI suggestions an episode carries, before a reviewer accepts them. */
export function useAIPendingSegments(episodeId: string | undefined) {
  return useQuery({
    queryKey: aiPendingSegmentsKey(episodeId),
    queryFn: async ({ signal }): Promise<EpisodeSegment[]> => {
      if (!episodeId) return [];
      return readSegments(episodeId, ['ai_pending'], signal);
    },
    enabled: !!episodeId,
  });
}

export function useAcceptAISegments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { episodeId: string; token: string }) => {
      const api = new BastionApiClient();
      return api.acceptEpisodeAISegments(params.episodeId, params.token);
    },
    onSuccess: (_data, variables) => {
      invalidateSegmentQueries(queryClient, variables.episodeId);
      void queryClient.invalidateQueries({
        queryKey: aiPendingSegmentsKey(variables.episodeId),
      });
      void queryClient.invalidateQueries({
        queryKey: cloudEpisodeKey(variables.episodeId),
      });
    },
  });
}

export function useRejectAISegments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { episodeId: string; token: string }) => {
      const api = new BastionApiClient();
      return api.rejectEpisodeAISegments(params.episodeId, params.token);
    },
    onSuccess: (_data, variables) => {
      invalidateSegmentQueries(queryClient, variables.episodeId);
      void queryClient.invalidateQueries({
        queryKey: aiPendingSegmentsKey(variables.episodeId),
      });
    },
  });
}
